import { useEffect, useState } from 'react';
import { List, X } from '@phosphor-icons/react';
import { useBranding } from '@/lib/branding/BrandingProvider';
import { cn } from '@/lib/utils/cn';
import { BeforeYouStartSection } from '@/features/guides/candidate/BeforeYouStartSection';
import { InstallAppSection } from '@/features/guides/candidate/InstallAppSection';
import { FirstTimeSetupSection } from '@/features/guides/candidate/FirstTimeSetupSection';
import { ConnectToInterviewSection } from '@/features/guides/candidate/ConnectToInterviewSection';
import { DuringInterviewSection } from '@/features/guides/candidate/DuringInterviewSection';
import { AfterInterviewSection } from '@/features/guides/candidate/AfterInterviewSection';
import { TroubleshootingSection } from '@/features/guides/candidate/TroubleshootingSection';

const SECTIONS = [
  { id: 'before-you-start', label: 'Before you start', Component: BeforeYouStartSection },
  { id: 'install', label: 'Install the app', Component: InstallAppSection },
  { id: 'first-time-setup', label: 'First-time setup', Component: FirstTimeSetupSection },
  {
    id: 'connect-to-interview',
    label: 'Connect to your interview',
    Component: ConnectToInterviewSection,
  },
  { id: 'during-interview', label: 'During the interview', Component: DuringInterviewSection },
  { id: 'after-interview', label: 'After the interview', Component: AfterInterviewSection },
  { id: 'troubleshooting', label: 'Troubleshooting', Component: TroubleshootingSection },
];

export default function CandidateGuidePage() {
  const brand = useBranding();
  const [tocOpen, setTocOpen] = useState(false);
  const [activeId, setActiveId] = useState<string>(SECTIONS[0].id);

  // Observe which section is currently on screen so the TOC can highlight it.
  useEffect(() => {
    const elements = SECTIONS.map((s) => document.getElementById(s.id)).filter(
      (el): el is HTMLElement => Boolean(el)
    );
    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length === 0) return;
        // Pick the one closest to the top of the viewport.
        const closest = visible.reduce((best, e) =>
          e.boundingClientRect.top < best.boundingClientRect.top ? e : best
        );
        setActiveId(closest.target.id);
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: 0 }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  function scrollToSection(id: string) {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setTocOpen(false);
  }

  return (
    <div className="relative min-h-screen bg-neurax-ink text-foreground">
      <div className="pointer-events-none absolute inset-0 bg-neurax-glow opacity-60" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[420px] grid-bg opacity-25" />

      <div className="relative mx-auto w-full max-w-6xl px-5 py-10 lg:px-8 lg:py-14">
        <header className="mb-10 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex items-center gap-4">
            {brand.logoUrl && (
              <img
                src={brand.logoUrl}
                alt={brand.name}
                className="h-12 w-auto max-w-[180px] object-contain drop-shadow-[0_0_18px_rgba(91,124,255,0.35)]"
              />
            )}
            <div>
              <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-neurax-electric/80">
                Candidate guide
              </p>
              <h1 className="font-display text-3xl font-semibold leading-tight text-white sm:text-4xl">
                How to use {brand.productName}
              </h1>
              <p className="mt-1 max-w-xl text-sm text-muted-foreground">
                Your interview intelligence buddy. Install, set up, and run it during your
                interviews — this guide walks you through every step.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setTocOpen((v) => !v)}
            aria-label="Toggle table of contents"
            className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs font-medium text-white transition hover:border-neurax-electric/40 hover:bg-neurax-electric/10 lg:hidden"
          >
            {tocOpen ? <X className="h-3.5 w-3.5" /> : <List className="h-3.5 w-3.5" />}
            Contents
          </button>
        </header>

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[220px,1fr]">
          <aside className={cn('lg:sticky lg:top-10 lg:self-start', !tocOpen && 'hidden lg:block')}>
            <nav className="glass-panel flex flex-col gap-1 p-2">
              {SECTIONS.map((section, idx) => {
                const active = section.id === activeId;
                return (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => scrollToSection(section.id)}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm transition',
                      active
                        ? 'bg-white/10 text-white shadow-glow-soft'
                        : 'text-muted-foreground hover:bg-white/5 hover:text-white'
                    )}
                  >
                    <span
                      className={cn(
                        'grid h-6 w-6 shrink-0 place-items-center rounded-full border text-[11px] font-semibold',
                        active
                          ? 'border-neurax-electric/60 bg-neurax-electric/10 text-neurax-electric'
                          : 'border-white/10 bg-white/5 text-muted-foreground'
                      )}
                    >
                      {idx + 1}
                    </span>
                    <span className="font-medium">{section.label}</span>
                  </button>
                );
              })}
            </nav>
          </aside>

          <main className="min-w-0 space-y-14">
            {SECTIONS.map(({ id, Component }) => (
              <Component key={id} />
            ))}

            <footer className="border-t border-white/5 pt-6 text-xs text-muted-foreground">
              Questions? Reach out to your recruiter. This guide is published by{' '}
              <strong className="text-white">{brand.productName}</strong>.
              {brand.supportEmail && (
                <>
                  {' '}
                  Support:{' '}
                  <a
                    className="text-neurax-electric underline underline-offset-2"
                    href={`mailto:${brand.supportEmail}`}
                  >
                    {brand.supportEmail}
                  </a>
                  .
                </>
              )}
            </footer>
          </main>
        </div>
      </div>
    </div>
  );
}
